// The outlet window (Outlets screen): its sections, what each one's status mark says, and a one-time
// request to open an outlet at a section from another screen (the Setup Checklist's Go).
// Status marks follow the Setup Checklist's rules (crs-doc setup design 5.2), so the two agree.

import type { DineOutletAdmin } from 'api/types';

export type OutletSection = 'general' | 'billing' | 'tax' | 'stations' | 'meals' | 'room';

export const OUTLET_SECTIONS: OutletSection[] = ['general', 'billing', 'tax', 'stations', 'meals', 'room'];

const KEY = 'crs.dineops.pendingOutletSection';

/** Asks the Outlets screen to open this outlet at this section the next time it loads. */
export const requestOutletSection = (outletId: string, section: string): void => {
  try { sessionStorage.setItem(KEY, JSON.stringify({ outletId, section })); } catch { /* non-fatal */ }
};

/** Takes (and clears) the pending request, if any. */
export const takeOutletSectionRequest = (): { outletId: string; section: OutletSection } | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const v = JSON.parse(raw);
    return v && typeof v.outletId === 'string' && OUTLET_SECTIONS.includes(v.section) ? v : null;
  } catch {
    return null;
  }
};

const MEAL_LABEL: Record<string, string> = { BREAKFAST: 'Breakfast', LUNCH: 'Lunch', DINNER: 'Dinner' };

/** "GST5 · SAC 996331 · incl." or null when no profile is in force. */
export const taxSummary = (o: DineOutletAdmin): string | null =>
  o.taxRateCode ? `${o.taxRateCode} · SAC ${o.taxSacCode ?? ''}${o.taxPricesIncludeTax ? ' · incl.' : ''}` : null;

export const buffetSummary = (o: DineOutletAdmin): string | null =>
  o.buffetMeals ? o.buffetMeals.split(',').map((m) => MEAL_LABEL[m] ?? m).join(', ') : null;

/** While tax is on, an outlet needs a tax profile: without one its bills are refused (checklist D6b). */
export const taxMissing = (o: DineOutletAdmin) => !!o.taxCalculationEnabled && !o.taxRateCode;
export const stationsMissing = (o: DineOutletAdmin) => (o.stationCount ?? 0) === 0;
export const roomChargeBlocked = (o: DineOutletAdmin) => o.pmsMode === 'ATTACHED' && !!o.propertyId && o.roomChargeAllowed !== true;
